import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { map } from 'ramda';
import { Field } from 'react-final-form';
import { bool, number, string, object } from 'prop-types';

import config from '../../config';

import css from './FieldTimePicker.css';

const displayTime = (time, use12Hours) => {
  const timeFormatted = use12Hours && time > 12 ? time - 12 : time
  return timeFormatted < 10 ? `0${timeFormatted}` : timeFormatted;
}

const FieldTimePickerComponent = props => {
  const {
    className,
    rootClassName,
    id,
    name,
    label,
    minuteStep,
    minHour,
    maxHour,
    required,
    input,
    ...rest,
  } = props;

  const [currentHour, setCurrentHour] = useState(input.value.hour);
  const [currentMinute, setCurrentMinute] = useState(input.value.minute);
  const [showHourPanel, setShowHourPanel] = useState(false);
  const [showMinutePanel, setShowMinutePanel] = useState(false);

  const use12Hours = config.locale === 'en';

  useEffect(() => {
    input.onChange({hour : currentHour, minute: currentMinute })
  }, [currentHour, currentMinute]);

  useEffect(() => {
    if (currentHour === maxHour) {
      setCurrentMinute(0);
    }
  }, [maxHour]);

  const handleOnClickHour = e => {
    const intValue = parseInt(e.target.value);
    setCurrentHour(intValue);
    if (intValue === maxHour) {
      setCurrentMinute(0);
    } else if (intValue === 24) {
      setCurrentMinute(0);
    }
    setShowHourPanel(false);
  }
  const handleOnClickMinute = e => {
    const intValue = parseInt(e.target.value);
    setCurrentMinute(intValue);
    setShowMinutePanel(false);
  }

  const handleOpenHourPanel = () => {
    setShowMinutePanel(false);
    setShowHourPanel(true);
    
  }
  const handleOpenMinutePanel = () => {
    setShowHourPanel(false);
    setShowMinutePanel(true);
  }

  const handleClickOutsidePanel = ref => {
    useEffect(() => {
      const handleClickOutside = e => {
        if (ref.current && !ref.current.contains(e.target)) {
          setShowHourPanel(false);
          setShowMinutePanel(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const wrapperRef = useRef(null);
  handleClickOutsidePanel(wrapperRef);
  
  const maxHourToShow = maxHour && minHour
    ? (maxHour + 1) - minHour
    : minHour
    ? 24 - minHour + 1
    : maxHour
    ? maxHour + 1
    : 24;
  const minHourToShow = minHour ? minHour : 0;
  const hourList = use12Hours
    ? map( hour => {
        if (hour > 12) {
          return { key: `${displayTime(hour - 12, use12Hours)} pm`, value: hour};
        } else {
          return { key: `${displayTime(hour, use12Hours)} am`, value: hour};
        };
      }, Array.from({length: maxHourToShow}, (_, i) => i + minHourToShow))
    : Array.from({length: maxHourToShow}, (_, i) => i + minHourToShow);

  const minuteStepToUse = minuteStep ? minuteStep : 1;
  const minuteNumber = Number.isInteger(60 / minuteStepToUse) ? 60 / minuteStepToUse : 60;
  const minuteList = currentHour === maxHour || currentHour === 24 ? [0] : Array.from({length: minuteNumber}, (_, i) => i * minuteStepToUse);

  const classes = classNames(rootClassName || css.root, className);
  const inputClasses = classNames(css.input, {
    [css.hourInputOpened]: showHourPanel,
    [css.minuteInputOpened]: showMinutePanel,
  });
  const panelClasses = classNames(css.timePanel, {
    [css.hourPanelOpened]: showHourPanel,
    [css.minutePanelOpened]: showMinutePanel,
    [css.hourPanel]: showHourPanel && use12Hours,
  });

  return (
    <div className={classes}>
      {label ? (
        <label>{label}</label>
      ) : null}
      <input className={css.hiddenInput} id={id} name={name} {...input} {...rest} />
      <div className={inputClasses}>
        <div className={css.timeContainer}>
          <button type='button' className={css.timeButton} onClick={handleOpenHourPanel}>
            {displayTime(currentHour, use12Hours)}
          </button>
          {showHourPanel ? (
            <div ref={wrapperRef} className={panelClasses}>
              <ul>
                {map(hour => {
                  const value = use12Hours ? hour.value : hour;
                  const displayValue = use12Hours ? hour.key : hour;
                  return (
                    <li key={value} className={css.listItem}>
                      <button type='button' value={value} onClick={handleOnClickHour}>
                        {use12Hours ? displayValue : displayTime(hour, use12Hours)}
                      </button>
                    </li>
                  )
                }, hourList)}
              </ul>
            </div>
          ) : null}
        </div>
        <div className={css.separator}>:</div>
        <div className={css.timeContainer}>
          <button type='button' className={css.timeButton} onClick={handleOpenMinutePanel}>
            {displayTime(currentMinute)}
          </button>
          {showMinutePanel ? (
            <div ref={wrapperRef} className={panelClasses}>
              <ul>
                {map(minute => (
                  <li key={minute} className={css.listItem}>
                    <button type='button' value={minute} onClick={handleOnClickMinute}>
                      {displayTime(minute)}
                    </button>
                  </li>
                ), minuteList)}
              </ul>
            </div>
          ) : null}
        </div>
        {use12Hours ? (
          <div className={css.use12Hours}>
            {currentHour > 12 ? 'pm' : 'am'}
          </div>
        ) : null}
      </div>
    </div>
  )
}

FieldTimePickerComponent.defaultProps = {
  className: null,
  rootClassName: null,
  minuteStep: 1,
};

FieldTimePickerComponent.propTypes = {
  className: string,
  rootClassName: string,
  id: string,
  name: string,
  label: string,
  minuteStep: number,
  minHour: number,
  maxHour: number,
  required: bool,
  use12Hours: bool,
  input: object,
};

const FieldTimePicker = props => {
  return <Field component={FieldTimePickerComponent} {...props} />;
};

export default FieldTimePicker;


// WEBPACK FOOTER //
// ./src/components/FieldTimePicker/FieldTimePicker.js